import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import {Dialog, DialogContent, DialogTitle} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {Colors, Spacing, Typography} from '../../../assets/styles'
import {borderRadiusMedium} from '../../../assets/styles/border'
import RXRButton from '../../RXRButton'

const useStyles = makeStyles(theme => ({
  item: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: Spacing.spaceMedium,
  },
  value: {
    flexShrink: 0,
    textAlign: 'center',
    height: 36,
    width: 36,
    borderRadius: '50%',
    lineHeight: '36px',
    fontSize: Typography.fontSizeMedium,
    fontWeight: Typography.heavyFontWeight,
  },
  details: {
    width: 'calc(100% - 36px)',
    paddingLeft: Spacing.spaceMedium,
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: Spacing.spaceSmall,
  },
  titleLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: Spacing.spaceExtraSmall,
  },
  titleText: {
    fontWeight: Typography.heavyFontWeight,
  },
  subtitleInline: {
    color: Colors.rxrMediumGreyColor,
    fontSize: Typography.fontSizeSmall,
  },
  date: {
    color: Colors.rxrMediumGreyColor,
    fontSize: Typography.fontSizeSmall,
    whiteSpace: 'nowrap',
  },
  body: {
    marginTop: Spacing.spaceExtraSmall,
    color: Colors.rxrDarkGreyColor,
    lineHeight: 1.4,
  },
  viewTranscript: {
    marginTop: Spacing.spaceExtraSmall,
    padding: 0,
    minWidth: 0,
  },
  dialog: {
    width: '100%',
    maxWidth: 720,
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: Spacing.spaceSmall,
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: Spacing.spaceSmall,
  },
  buildingName: {
    fontWeight: Typography.heavyFontWeight,
  },
  headerDivider: {
    width: 1,
    height: 28,
    backgroundColor: Colors.rxrGreyColor,
  },
  userName: {
    fontWeight: Typography.heavyFontWeight,
  },
  userUnit: {
    color: Colors.rxrMediumGreyColor,
    fontSize: Typography.fontSizeSmall,
  },
  dialogCloseButton: {
    padding: 0,
    minWidth: 0,
    height: 'auto',
  },
  transcriptContainer: {
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  messageRow: {
    padding: `${Spacing.spaceSmall}px 0`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '100%',
  },
  messageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: Spacing.spaceExtraSmall,
    fontSize: Typography.fontSizeSmall,
    color: Colors.rxrMediumGreyColor,
  },
  messageSender: {
    fontWeight: Typography.heavyFontWeight,
    color: Colors.rxrMediumGreyColor,
  },
  messageTimestamp: {
    color: Colors.rxrMediumGreyColor,
    fontSize: Typography.fontSizeSmall,
  },
  messageBubble: {
    marginTop: Spacing.spaceExtraSmall,
    padding: `${Spacing.spaceExtraSmall}px ${Spacing.spaceSmall}px`,
    borderRadius: borderRadiusMedium,
    maxWidth: '80%',
    color: Colors.rxrBlackColor,
    backgroundColor: Colors.messageBubbleColor,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: 1.4,
  },
  messageBubbleStaff: {
    alignSelf: 'flex-end',
    backgroundColor: Colors.messageBubbleColorPeer,
  },
  transcriptText: {
    whiteSpace: 'pre-wrap',
    color: Colors.rxrDarkGreyColor,
  },
  transcriptEmpty: {
    color: Colors.rxrMediumGreyColor,
  },
}))

const formatDate = value => {
  const date = moment(value)
  return date.isValid() ? date.format('M/D/YY') : ''
}

const formatTimestamp = value => {
  const date = moment(value)
  return date.isValid() ? date.format('M/D/YY h:mmA') : ''
}

function ConversationSentimentItem({item, color}) {
  const classes = useStyles()
  const [isOpen, setIsOpen] = useState(false)

  const hasTranscript = Boolean((item?.transcriptMessages && item.transcriptMessages.length > 0) || item?.transcriptText)

  const formattedDate = useMemo(() => formatDate(item?.date), [item?.date])
  const transcriptMessages = item?.transcriptMessages || []
  const userMessage = useMemo(
    () => transcriptMessages.find(message => message?.resident_or_staff === 'resident'),
    [transcriptMessages],
  )
  const userName =
    userMessage?.sender_name && userMessage.sender_name !== 'Unknown' ? userMessage.sender_name : 'Resident'
  const userUnit = userMessage?.resident_unit_number || ''

  const handleOpen = () => {
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  return (
    <div className={classes.item}>
      <div className={classes.value} style={{backgroundColor: color || Colors.magentaLight}}>
        {item?.metric}
      </div>
      <div className={classes.details}>
        <div className={classes.headerRow}>
          <div className={classes.titleLine}>
            <span className={classes.titleText}>{item?.title || 'Conversation'}</span>
            {item?.subtitle ? <span className={classes.subtitleInline}>{item.subtitle}</span> : null}
          </div>
          {formattedDate ? <span className={classes.date}>{formattedDate}</span> : null}
        </div>
        {item?.body ? <div className={classes.body}>{item.body}</div> : null}
        {hasTranscript ? (
          <RXRButton className={classes.viewTranscript} type={RXRButton.TYPE_TEXT} onClick={handleOpen}>
            View transcript
          </RXRButton>
        ) : null}
      </div>

      <Dialog open={isOpen} onClose={handleClose} classes={{paper: classes.dialog}}>
        <DialogTitle className={classes.dialogTitle}>
          <div className={classes.headerContent}>
            <div className={classes.buildingName}>{item?.subtitle || ''}</div>
            <div className={classes.headerDivider} />
            <div>
              <div className={classes.userName}>{userName}</div>
              <div className={classes.userUnit}>{userUnit}</div>
            </div>
          </div>
          <RXRButton className={classes.dialogCloseButton} type={RXRButton.TYPE_TEXT_SECONDARY} onClick={handleClose}>
            Close
          </RXRButton>
        </DialogTitle>
        <DialogContent className={classes.transcriptContainer}>
          {transcriptMessages.length > 0 ? (
            transcriptMessages.map((message, index) => {
              const timestamp = formatTimestamp(message?.created_at)
              const senderName = message?.sender_name || 'Unknown'
              const senderLabel =
                message?.resident_or_staff === 'resident' && message?.resident_unit_number
                  ? `${senderName}, ${message.resident_unit_number}`
                  : senderName
              const staffMessage = message?.resident_or_staff === 'staff'
              return (
                <div className={classes.messageRow} key={`${item?.id || 'conversation'}-${index}`}>
                  <div className={classes.messageHeader}>
                    <span className={classes.messageSender}>{senderLabel}</span>
                    {timestamp ? <span className={classes.messageTimestamp}>{timestamp}</span> : null}
                  </div>
                  <div
                    className={`${classes.messageBubble} ${staffMessage ? classes.messageBubbleStaff : ''}`}
                  >
                    {message?.content || 'Message unavailable.'}
                  </div>
                </div>
              )
            })
          ) : item?.transcriptText ? (
            <div className={classes.transcriptText}>{item.transcriptText}</div>
          ) : (
            <div className={classes.transcriptEmpty}>Transcript unavailable.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

ConversationSentimentItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    body: PropTypes.string,
    metric: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    transcriptMessages: PropTypes.arrayOf(
      PropTypes.shape({
        content: PropTypes.string,
        sender_name: PropTypes.string,
        resident_unit_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
        resident_or_staff: PropTypes.string,
      }),
    ),
    transcriptText: PropTypes.string,
  }),
  color: PropTypes.string,
}

ConversationSentimentItem.defaultProps = {
  item: null,
  color: null,
}

export default ConversationSentimentItem
